import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { RANDOM_COLOR } from '../utils/celestialRandomBaseline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import { API_BASE_URL } from '../constants';

/**
 * DEEP CELESTIAL RESEARCH — the selection UI.
 *
 * <h2>The trine has no checkbox, because it is the anchor</h2>
 *
 * The study asks "a trine, and one or more of these, on the same day". The trine is what the
 * others are matched AGAINST, so it is always required and offering a box to turn it off would
 * offer a study this does not compute. With nothing else ticked the answer is the plain trine
 * list, which is the brief's own item 1 — so the panel says that rather than appearing inert.
 *
 * <h2>Common and Different are a filter, not a mode</h2>
 *
 * Every matched pair either shares a body or does not, so ticking BOTH and ticking NEITHER are
 * the same request: no constraint. Stated on screen, because a reader who ticks neither and sees
 * everything would otherwise reasonably conclude the boxes do nothing.
 */
export const ASPECT_CHOICES = [
  { key: 'CONJUNCTION', label: 'Conjunction', hint: '0° — the two bodies at the same longitude.' },
  { key: 'SQUARE', label: 'Square', hint: '90° and 270° — both are squares, and both are searched.' },
  { key: 'OPPOSITION', label: 'Opposition', hint: '180°.' },
];

/**
 * GEOCENTRIC IS THE DEFAULT, and the two frames are not interchangeable views of one answer.
 *
 * <p>They change which ASPECTS ARE POSSIBLE: geocentrically Venus never leaves ~47 degrees of
 * the Sun and Mercury ~28, so a Sun–Venus trine cannot occur at all. Heliocentrically there is
 * no such limit, because the Sun is the origin rather than a body.
 *
 * <p>They also change WHICH BODIES take part — see the hints. Three drop out heliocentrically
 * and one geocentrically, each because its position in that frame is undefined or degenerate.
 */
export const FRAME_CHOICES = [
  { key: 'GEOCENTRIC', label: 'Geocentric',
    hint: 'As seen from Earth — the default, and what every figure here was measured in. '
      + 'Sun, Moon, the planets, Rahu and Ketu.' },
  { key: 'HELIOCENTRIC', label: 'Heliocentric',
    hint: 'As seen from the Sun. The Sun drops out (it is the origin) and Earth takes its place; '
      + 'the Moon and the nodes drop out too — the Moon sits within a sixtieth of a degree of '
      + 'Earth from there, and the nodes are defined by the Moon\'s orbit around Earth.' },
];

/**
 * Where the marks are drawn. See {@code CELESTIAL_PLACEMENT} for why this is not cosmetic.
 */
export const PLACEMENT_CHOICES = [
  { key: 'BARS', label: 'On the bars',
    hint: 'A triangle above each marked bar. Compact, but a downward triangle over a high reads '
      + 'as a SHORT — and these aspects carry no direction.' },
  { key: 'RAIL', label: 'Rail at the bottom',
    hint: 'All marks on one line at the foot of the panel, each with a thin vertical through its '
      + 'bar. Says WHICH bar without implying which way.' },
];

export const MODIFIER_CHOICES = [
  { key: 'common', label: 'Common planet',
    hint: 'The trine and the matched aspect share a body — trine Mars–Saturn with square '
      + 'Mars–Venus.' },
  { key: 'different', label: 'Different planet',
    hint: 'They share no body at all — trine Mars–Saturn with square Venus–Jupiter.' },
  { key: 'includeMoon', label: 'Include Moon',
    hint: 'Applies to BOTH sides. Off, the Moon takes part in neither the trine nor the matched '
      + 'aspect — leaving it on one side would match nearly every trine in the series.' },
];

const CelestialAspectDialog = ({ open, onClose, isAdmin, token, from, to, barCount,
                                 onPaint, onPaintRandom }) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [aspects, setAspects] = useState(
    () => Object.fromEntries(ASPECT_CHOICES.map((a) => [a.key, false])));
  const [modifiers, setModifiers] = useState(
    { common: false, different: false, includeMoon: false });
  const [frame, setFrame] = useState('GEOCENTRIC');
  const [placement, setPlacement] = useState('BARS');

  /**
   * Whether a random baseline is showing instead of the study.
   *
   * <p>A BOOLEAN, not the draw itself. The draw is rolled where the real overlay is built,
   * because that is the only place its size is knowable — the baseline matches the study's own
   * mark count, and only the side holding the bars can count those.
   */
  const [randomOn, setRandomOn] = useState(false);

  /** Supersession guard — see the Deep Pivot dialog; a slow scan must not land under new boxes. */
  const scanId = useRef(0);

  /**
   * Held in a REF so it cannot enter `run`'s dependency array. A parent passing an inline
   * `onPaint={() => ...}` makes a new function every render, which would change `run`'s identity,
   * fire the effect, scan, render, and scan again — an unbounded loop reached by the ordinary
   * way of passing a prop. The Deep Pivot dialog carries the same guard for the same reason.
   */
  const paintRef = useRef(onPaint);
  paintRef.current = onPaint;

  /**
   * PLACEMENT IS A DRAWING CHOICE, NOT A QUERY ONE, and it is held in a ref for that reason.
   *
   * <p>Putting it in `run`'s dependency array would re-fetch the whole calendar to move marks
   * that are already on the chart — up to eight seconds to redraw the same answer. The effect
   * below repaints from the report already in hand instead.
   */
  const placementRef = useRef(placement);
  placementRef.current = placement;

  const selected = ASPECT_CHOICES.filter((a) => aspects[a.key]).map((a) => a.key).join(',');

  /** Held in a ref for the same reason as onPaint — see the note above. */
  const paintRandomRef = useRef(onPaintRandom);
  paintRandomRef.current = onPaintRandom;

  /**
   * THE ACTIVE DRAW, mirrored into a ref.
   *
   * <p>`run` has to know whether a baseline is showing so it paints the right thing when a scan
   * lands, and it cannot take `randomOn` as a dependency: that would change `run`'s identity,
   * fire the scan effect, scan again and repaint — the unbounded loop the `onPaint` ref already
   * guards against, reached a second way.
   */
  const randomOnRef = useRef(false);
  randomOnRef.current = randomOn;

  /** The modifiers a paint should filter by, mirrored for the same reason. */
  const modifiersRef = useRef(modifiers);
  modifiersRef.current = modifiers;

  /**
   * Rolls a FRESH baseline and paints it.
   *
   * <p>Every click re-rolls, which is the whole point: one draw is one sample, and a reader
   * forming a view from a single scatter is doing exactly what the button exists to guard
   * against. Clicking it repeatedly is the cheapest version of the test.
   */
  const rollRandom = useCallback(() => {
    if (!report) {
      setError('Run the study first — the baseline is sized to match what it marked.');
      return;
    }
    setRandomOn(true);
    setError('');
    if (typeof paintRandomRef.current === 'function') {
      paintRandomRef.current(report, modifiers, placementRef.current);
    }
  }, [report, modifiers]);

  /** Clears the baseline and puts the real study back, from the report already in hand. */
  const restoreActual = useCallback(() => {
    setRandomOn(false);
    if (typeof paintRef.current === 'function') paintRef.current(report, placementRef.current);
  }, [report]);

  const run = useCallback(async () => {
    if (!from || !to) return;
    const mine = (scanId.current += 1);
    setRunning(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/api/celestial-aspects`
        + `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        + `&aspects=${encodeURIComponent(selected)}`
        // Sent explicitly in both directions, like every other lever in this app: a falsy check
        // that dropped `false` would hand the decision to the server's default.
        + `&common=${modifiers.common ? 'true' : 'false'}`
        + `&different=${modifiers.different ? 'true' : 'false'}`
        + `&includeMoon=${modifiers.includeMoon ? 'true' : 'false'}`
        + `&frame=${encodeURIComponent(frame)}`;
      const response = await fetch(url, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          if (body && body.error) detail = body.error;
        } catch (ignored) { /* a non-JSON error body is not worth a second failure */ }
        throw new Error(detail);
      }
      const body = await response.json();
      if (mine !== scanId.current) return;
      setReport(body);
      // A SCAN THAT LANDS WHILE A BASELINE IS SHOWING MUST NOT PAINT OVER IT.
      //
      // Every control re-runs the scan, so ticking Common with a draw on screen would otherwise
      // replace the grey marks with the real study — the toggle would look like it had cleared
      // the baseline. Instead the SAME draw is re-filtered by the new modifiers, which is what
      // makes it possible to see one scatter under both filters.
      if (randomOnRef.current) {
        if (typeof paintRandomRef.current === 'function') {
          // RE-ROLLED at the new size, deliberately. A filter change changes how many marks the
          // study draws, so a size-matched baseline MUST be re-drawn — keeping the old scatter
          // would be comparing the new real set against a baseline sized for the old one, which
          // is the mis-sizing this whole change removes.
          paintRandomRef.current(body, modifiersRef.current, placementRef.current);
        }
      } else if (typeof paintRef.current === 'function') {
        paintRef.current(body, placementRef.current);
      }
    } catch (e) {
      if (mine !== scanId.current) return;
      setError(e.message || String(e));
    } finally {
      if (mine === scanId.current) setRunning(false);
    }
  }, [from, to, token, selected, modifiers, frame]);

  /**
   * THE CONTROLS RE-RUN THE SCAN. Keyed on `run`, whose identity already changes with every
   * lever — so a control added later is covered without a second dependency list to remember.
   */
  useEffect(() => {
    if (!open || !isAdmin || !token) return;
    run();
  }, [open, isAdmin, token, run]);

  /**
   * REPAINT ON PLACEMENT, from the report already held. No fetch: the marks do not change, only
   * where they are drawn.
   */
  useEffect(() => {
    if (!open) return;
    // The baseline follows the placement control too — a reader switching to the rail must get
    // the rail for whichever set is on screen, not be silently handed the other one.
    if (randomOn) {
      if (report && typeof paintRandomRef.current === 'function') {
        paintRandomRef.current(report, modifiers, placement);
      }
      return;
    }
    if (!report) return;
    if (typeof paintRef.current === 'function') paintRef.current(report, placement);
  }, [placement, report, open, randomOn, modifiers]);

  if (!isAdmin) return null;

  const dayLists = (report && report.dayLists) || {};
  const bothModifiers = modifiers.common && modifiers.different;
  const neitherModifier = !modifiers.common && !modifiers.different;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Deep Celestial Research — trine confluence
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Precise aspects between 12 bodies, filed by day on an 18:00 America/New_York cutoff.
          Admin only.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Coordinate system</Typography>
          <RadioGroup row value={frame} onChange={(e) => setFrame(e.target.value)}>
            {FRAME_CHOICES.map((f) => (
              <FormControlLabel key={f.key} value={f.key} disabled={running}
                control={<Radio size="small" data-testid={`celestial-frame-${f.key}`} />}
                label={f.label} />
            ))}
          </RadioGroup>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', maxWidth: 680 }}>
            {(FRAME_CHOICES.find((f) => f.key === frame) || {}).hint}
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5 }}>
            Marker placement
          </Typography>
          <RadioGroup row value={placement} onChange={(e) => setPlacement(e.target.value)}>
            {PLACEMENT_CHOICES.map((p) => (
              <FormControlLabel key={p.key} value={p.key} disabled={running}
                control={<Radio size="small" data-testid={`celestial-placement-${p.key}`} />}
                label={p.label} />
            ))}
          </RadioGroup>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', maxWidth: 680 }}>
            {(PLACEMENT_CHOICES.find((p) => p.key === placement) || {}).hint}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Match the trine against
            </Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 0.5, maxWidth: 330 }}>
              The TRINE is always required — it is what these are matched against. With none
              ticked you get the plain trine list.
            </Typography>
            <FormGroup>
              {ASPECT_CHOICES.map((a) => (
                <Box key={a.key}>
                  <FormControlLabel
                    control={(
                      <Checkbox size="small" disabled={running}
                        data-testid={`celestial-aspect-${a.key}`}
                        checked={!!aspects[a.key]}
                        onChange={(e) => setAspects(
                          (prev) => ({ ...prev, [a.key]: e.target.checked }))} />
                    )}
                    label={a.label}
                  />
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', ml: 3.75, mb: 0.5, maxWidth: 300 }}>
                    {a.hint}
                  </Typography>
                </Box>
              ))}
            </FormGroup>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Restrict to</Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 0.5, maxWidth: 330 }}>
              {bothModifiers || neitherModifier
                ? 'Both ticked, or neither, is NO CONSTRAINT — every matched pair is one or the '
                  + 'other, so the two together are the whole set.'
                : 'Filtering matched pairs by whether they share a body.'}
            </Typography>
            <FormGroup>
              {MODIFIER_CHOICES.map((m) => (
                <Box key={m.key}>
                  <FormControlLabel
                    control={(
                      <Checkbox size="small" disabled={running}
                        data-testid={`celestial-mod-${m.key}`}
                        checked={!!modifiers[m.key]}
                        onChange={(e) => setModifiers(
                          (prev) => ({ ...prev, [m.key]: e.target.checked }))} />
                    )}
                    label={m.label}
                  />
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', ml: 3.75, mb: 0.5, maxWidth: 300 }}>
                    {m.hint}
                  </Typography>
                </Box>
              ))}
            </FormGroup>
          </Box>
        </Box>

        {running && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="caption" color="text.secondary">
              Searching the ephemeris — the first run over a long range takes a few seconds, and
              the result is cached, so changing a box after that is instant.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        {report && !error && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>{report.matchCount}</strong> day{report.matchCount === 1 ? '' : 's'} marked
            {' '}from <strong>{report.eventCount}</strong> precise aspects between{' '}
            {report.from} and {report.to}.
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              Aspect day counts (time removed, 18:00 ET cutoff) —{' '}
              {Object.entries(dayLists)
                .map(([kind, days]) => `${kind.toLowerCase()}: ${days.length}`)
                .join(' · ')}
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {report.frame === 'HELIOCENTRIC' ? 'Heliocentric' : 'Geocentric'} —{' '}
              {(report.bodies || []).length} bodies
              {(report.excludedBodies || []).length > 0
                ? `, excluding ${report.excludedBodies.join(', ')} as undefined or degenerate `
                  + 'in this frame'
                : ''}.
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.85 }}>
              Rahu and Ketu are excluded as a PAIR — they sit exactly 180° apart at every instant,
              so that one pair would put an opposition on every day of the calendar. Both still
              aspect everything else.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/*
          THE BASELINE CONTROLS, on the left and visually apart from the study's own actions.
          They do not change what the study computes; they replace what is DRAWN with a control
          group, which is a different kind of action from re-running a scan.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={rollRandom}
            size="small"
            variant={randomOn ? 'contained' : 'outlined'}
            color="inherit"
            disabled={running || !report}
            data-testid="celestial-random-roll"
            sx={{ color: RANDOM_COLOR, borderColor: RANDOM_COLOR }}
          >
            {randomOn ? 'Re-roll random' : 'Random signal'}
          </Button>
          <Button
            onClick={restoreActual}
            size="small"
            disabled={running || !randomOn}
            data-testid="celestial-random-restore"
          >
            Restore actual
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 330 }}>
            {randomOn
              ? `Grey marks scattered at random over ${barCount} bars, SIZE-MATCHED to what the `
                + 'study just drew and split the same way. Re-roll for another sample — a '
                + 'visual baseline, not a statistical test.'
              : 'Scatters the SAME NUMBER of marks at random, in grey, to show what this study '
                + 'looks like when it means nothing.'}
          </Typography>
        </Box>

        <Box>
          <Button onClick={run} size="small" disabled={running}>Re-run</Button>
          <Button onClick={onClose} size="small">Close</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

CelestialAspectDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  token: PropTypes.string,
  /** The chart's own first and last bar date, as YYYY-MM-DD. */
  from: PropTypes.string,
  to: PropTypes.string,
  /** The chart's bar count. The random draw's upper bound — never hardcoded. */
  barCount: PropTypes.number,
  onPaint: PropTypes.func,
  onPaintRandom: PropTypes.func,
};

CelestialAspectDialog.defaultProps = {
  open: false,
  isAdmin: false,
  token: '',
  from: '',
  to: '',
  barCount: 0,
  onPaint: undefined,
  onPaintRandom: undefined,
};

export default CelestialAspectDialog;
